import { format, parseISO } from "date-fns";

export const ADDRESS_STATUSES = [
  { value: "scheduled", label: "Scheduled" },
  { value: "in_progress", label: "In Progress" },
  { value: "customer_home", label: "Customer Home" },
  { value: "walk_away", label: "Walk Away" },
  { value: "completed", label: "Completed" },
  { value: "cancelled", label: "Cancelled" },
];

export const RUN_STATUSES = [
  { value: "scheduled", label: "Scheduled" },
  { value: "in_progress", label: "In Progress" },
  { value: "completed", label: "Completed" },
  { value: "cancelled", label: "Cancelled" },
];

export const STATUS_LABELS = Object.fromEntries(
  [...ADDRESS_STATUSES, ...RUN_STATUSES].map((s) => [s.value, s.label])
);

export const TEAM_ROLE_LABELS = {
  locksmith: "Locksmith",
  agent: "Agent",
  engineer: "Engineer",
  dog_handler: "Dog Handler",
  manager: "Manager",
};

export const TEAM_ROLE_PLURALS = {
  locksmith: "Locksmiths",
  agent: "Agents",
  engineer: "Engineers",
  dog_handler: "Dog Handlers",
};

export const WORKER_ROLE_OPTIONS = [
  ["agent", "Agent"],
  ["engineer", "Engineer"],
  ["locksmith", "Locksmith"],
  ["dog_handler", "Dog Handler"],
];

export const ENGINEER_JOB_TYPES = ["Dual", "Electric", "Gas", "Removal"];

export const STATUS_STYLES = {
  scheduled: "bg-blue-50 text-blue-700 border-blue-200 [--dot:theme(colors.blue.500)]",
  in_progress: "bg-amber-50 text-amber-800 border-amber-300 [--dot:theme(colors.amber.500)]",
  customer_home: "bg-indigo-50 text-indigo-800 border-indigo-300 [--dot:theme(colors.indigo.500)]",
  completed: "bg-emerald-50 text-emerald-800 border-emerald-300 [--dot:theme(colors.emerald.500)]",
  walk_away: "bg-rose-50 text-rose-800 border-rose-300 [--dot:theme(colors.rose.500)]",
  cancelled: "bg-zinc-100 text-zinc-600 border-zinc-300 [--dot:theme(colors.zinc.400)]",
};

const FINAL = ["completed", "walk_away", "cancelled"];

export function progressOf(addresses = []) {
  const total = addresses.length;
  const done = addresses.filter((a) => FINAL.includes(a.status) || (a.status === "customer_home" && a.ended_at)).length;
  return { total, done, pct: total ? Math.round((done / total) * 100) : 0 };
}

export const START_TIMES = Array.from({ length: 25 }, (_, i) => {
  const mins = 6 * 60 + i * 30;
  const hh = String(Math.floor(mins / 60)).padStart(2, "0");
  const mm = String(mins % 60).padStart(2, "0");
  return `${hh}:${mm}:00`;
});

export function formatTime(t) {
  if (!t) return "—";
  const [h, m] = t.split(":").map(Number);
  const suffix = h >= 12 ? "pm" : "am";
  return `${((h + 11) % 12) + 1}:${String(m).padStart(2, "0")} ${suffix}`;
}

export function formatDate(d, pattern = "EEE d MMM yyyy") {
  return d ? format(parseISO(d), pattern) : "—";
}

export const todayISO = () => format(new Date(), "yyyy-MM-dd");

const FUTURE_ALLOWED = ["scheduled", "cancelled"];
export const isFutureRun = (runDate) => !!runDate && runDate > todayISO();
export const statusAllowed = (runDate, status) => !isFutureRun(runDate) || FUTURE_ALLOWED.includes(status);
export const FUTURE_STATUS_HINT = "Future run: only Scheduled or Cancelled until the day of the run.";

const POSTCODE_RE = /^[A-Z]{1,2}\d[A-Z\d]?\s*\d[A-Z]{2}$/i;
const OUTWARD_RE = /^[A-Z]{1,2}\d[A-Z\d]?$/i;
export const isValidPostcode = (p) => POSTCODE_RE.test((p || "").trim());
export const isValidPostcodeOrArea = (p) => isValidPostcode(p) || OUTWARD_RE.test((p || "").trim());

export function normalizePostcode(p) {
  const clean = (p || "").toUpperCase().replace(/\s+/g, "");
  if (OUTWARD_RE.test(clean)) return clean;
  return clean.length > 3 ? `${clean.slice(0, -3)} ${clean.slice(-3)}` : clean;
}

export const parseList = (s) =>
  (s || "").split(",").map((x) => x.trim().toUpperCase()).filter(Boolean);

export const EMAIL_RE = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
